<template>
<div class="community-wrap" :class="{ 'cta-band': !store.querent_id }">

  <!-- Community navigation tabs -->
  <div class="community-tabs">
    <button :class="['community-tab', { active: communityView === 'seekers' }]" @click="handleSeekerFeed">{{ LESSONS_TAB }}</button>
    <button :class="['community-tab', { active: communityView === 'home' }]" @click="goHome">Mystics</button>
    <button :class="['community-tab', { active: communityView === 'pulse' }]" @click="emit('show-pulse')">Voice</button>
  </div>

  <!-- Mystic Drop Detail Page -->
  <div v-if="communityView === 'drops' && dailyDrop" class="drop-detail">
    <button v-if="dropReaderSlug !== 'iam'" class="drop-detail-back" @click="returnToAidaDrop">← Back to Mystic AIda</button>

    <div class="drop-detail-body">
      <!-- Tarot card + card name/date + reading side by side -->
      <div class="drop-detail-top">
        <div class="drop-detail-text">
          <span class="drop-detail-card-name" v-if="dailyDrop.tarot_card">
            {{ dailyDrop.tarot_card }} · {{ dailyDrop.drop_type === 'natal' ? 'Natal' : 'Zodiac' }} Reading
            <span class="drop-detail-info-tip" title="Each Mystic Drop combines a tarot card pull with an astrology reading - zodiac uses your sun sign, natal uses your full birth chart.">?</span>
          </span>
          <span class="drop-detail-card-date">{{ new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' }) }}</span>
          <p class="drop-detail-reading">{{ dailyDrop.reading_text }}</p>
        </div>
        <div class="drop-detail-img-wrap" v-if="dailyDrop.tarot_image_url" @click="showImageModal = true">
          <img :src="dailyDrop.tarot_image_url" :alt="dailyDrop.tarot_card" class="drop-detail-hero-img" />
        </div>
      </div>

      <!-- Mystic AIda: Reader drops -->
      <div v-if="dropReaderSlug === 'iam'" class="drop-detail-mystics">
        <h3 id="discover-mystic-drops" class="drop-detail-mystics-title">Discover Mystic Drops</h3>
        <div class="drop-detail-reader-grid">
          <div
            v-for="reader in visibleReaders"
            :key="reader.sub_domain"
            class="drop-detail-reader-card"
            @click="emit('open-reader-detail', reader.sub_domain)"
          >
            <div class="drop-detail-reader-img">
              <h4 class="drop-detail-reader-name" v-if="reader.today_drop?.title">{{ reader.today_drop.title }}</h4>
              <img
                v-if="reader.today_drop && (reader.today_drop.drop_preview_image_url || reader.today_drop.drop_image_url)"
                :src="reader.today_drop.drop_preview_image_url || reader.today_drop.drop_image_url"
                :alt="reader.today_drop.title"
              />
              <img
                v-else-if="reader.background_image"
                :src="reader.background_image"
                :alt="reader.name"
              />
              <div v-else class="drop-detail-reader-placeholder"></div>
            </div>
            <div class="drop-detail-reader-btn-wrap">
              <button class="drop-detail-reader-btn">View {{ reader.name }}'s Mystic Drop</button>
            </div>
          </div>
        </div>
        <div v-if="hasMoreReaders" ref="loadMoreEl" class="drop-detail-load-more">
          Loading more mystics...
        </div>
        <div v-else class="drop-detail-load-more drop-detail-end">
          No More Mystic Drops
        </div>
      </div>

      <!-- Other readers: Zodiac Sign Horoscopes -->
      <div class="drop-detail-signs" v-else-if="dailyDrop.horoscopes">

        <!-- No sign selected: pick your sign grid -->
        <div v-if="!selectedSign" class="sign-picker">
          <span class="drop-detail-signs-label">What's your sign?</span>
          <div class="sign-picker-grid">
            <div
              v-for="z in zodiacOrder"
              :key="z.key"
              class="sign-picker-item"
              @click="selectedSign = z.key"
            >
              <img v-if="dailyDrop.horoscopes[z.key]?.image_url" :src="dailyDrop.horoscopes[z.key].image_url" :alt="z.key" class="sign-picker-img" />
              <div v-else class="sign-picker-placeholder">{{ z.glyph }}</div>
              <span class="sign-picker-name">{{ dailyDrop.horoscopes[z.key]?.reader_sign || z.key }}</span>
              <span class="sign-picker-dates">{{ z.dates }}</span>
            </div>
          </div>
        </div>

        <!-- Sign selected: show their reading + dropdown to switch -->
        <div v-else class="sign-reading">
          <div class="sign-reading-selector" @click="showSignDropdown = !showSignDropdown">
            <img v-if="dailyDrop.horoscopes[selectedSign]?.image_url" :src="dailyDrop.horoscopes[selectedSign].image_url" class="sign-reading-current-img" />
            <div class="sign-reading-current-info">
              <span class="sign-reading-current-name">{{ dailyDrop.horoscopes[selectedSign]?.reader_sign || selectedSign }}</span>
              <span class="sign-reading-current-dates">{{ zodiacDates(selectedSign) }}</span>
            </div>
            <span class="sign-reading-chevron">{{ showSignDropdown ? '▲' : '▼' }}</span>
          </div>

          <!-- Dropdown -->
          <Transition name="sign-fade">
            <div v-if="showSignDropdown" class="sign-dropdown">
              <div
                v-for="z in zodiacOrder"
                :key="z.key"
                class="sign-dropdown-item"
                :class="{ active: z.key === selectedSign }"
                @click="selectedSign = z.key; showSignDropdown = false"
              >
                <img v-if="dailyDrop.horoscopes[z.key]?.image_url" :src="dailyDrop.horoscopes[z.key].image_url" class="sign-dropdown-img" />
                <span class="sign-dropdown-name">{{ dailyDrop.horoscopes[z.key]?.reader_sign || z.key }}</span>
                <span class="sign-dropdown-dates">{{ z.dates }}</span>
              </div>
            </div>
          </Transition>

          <!-- The reading -->
          <div class="sign-reading-body">
            <p class="sign-reading-text">{{ dailyDrop.horoscopes[selectedSign]?.text }}</p>
          </div>
        </div>
      </div>
    </div>
  </div>

  <LoadState
    v-if="communityView === 'drops' && !dailyDrop"
    :loading="dropLoading" :failed="dropFailed"
    message="Couldn't reach today's drop just now."
    loading-label="Drawing today's drop"
    @retry="fetchDailyDrop(dropReaderSlug)"
  />

  <!-- The Mystics tab (2026-10-26, Claude outputs/mystics-tab-2026-10-26/):
       Your Mystics, the box, Everyone here. The Featured carousel, "Reader #",
       reading counts and the per-card Get Reading are gone. -->
  <div v-if="communityView === 'home'" class="discover-mystics">
    <div class="discover-tabs-spacer"></div>
    <MysticsTab
      @open-mystic="emit('open-reader-detail', $event)"
      @open-page="emit('open-reader-detail', $event)"
      @ask-mystic="emit('ask-mystic', $event)"
      @open-lesson="emit('open-lesson', $event)"
      @view-deck-gallery="emit('view-deck-gallery', $event)"
      @show-lessons="handleSeekerFeed"
      @request-auth="emit('request-auth', $event)"
      @home-changed="emit('home-changed', $event)"
    />
  </div>

  <!-- Lessons (community-simplify DESIGN.md §3): one search, seven topics, a
       slim Fireside row and one stream of lessons. Nothing names a person. -->
  <div v-if="communityView === 'seekers'" class="lessons-pane">
    <YourMysticsRow @open-mystic="emit('open-reader-detail', $event)" />
    <LessonsTab
      @open-lesson="emit('open-lesson', $event)"
      @ask="(words, reader, via) => emit('ask-cards', words, reader, via)"
    />
  </div>

  </div>

<!-- Fullscreen Image Modal -->
<Teleport to="body">
  <div v-if="showImageModal && (dailyDrop?.tarot_image_url || dailyDrop?.image_url)" class="image-modal-overlay" @click="showImageModal = false">
    <img :src="dailyDrop.tarot_image_url || dailyDrop.image_url" :alt="dailyDrop.tarot_card || dailyDrop.title" class="image-modal-img" />
  </div>
</Teleport>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { store, static_store } from '@/store'

import LessonsTab from '@/components/ui/community/LessonsTab.vue'
import YourMysticsRow from '@/components/ui/community/YourMysticsRow.vue'
import LoadState from '@/components/ui/LoadState.vue'
import { useLoadState } from '@/composables/useLoadState'
import MysticsTab from '@/components/ui/community/MysticsTab.vue'
import { TAB_LABEL as LESSONS_TAB } from '@/utils/community-lessons'

const props = defineProps({
  pulse: { type: Object, default: null },
  circleFeed: { type: Array, default: () => [] },
  activeQuest: { type: Object, default: null },
  communityVibes: { type: Object, default: null },
  mysticAidaImageUrl: { type: String, default: null },
  communityMessages: { type: Array, default: () => [] },
  initialView: { type: String, default: null },
  // The page's remembered tab: this panel unmounts for a Mystic's page or Voice.
  tab: { type: String, default: 'seekers' },
})

watch(() => props.initialView, (view) => {
  if (view === 'seekers') handleSeekerFeed()
  else if (view === 'mystics') goHome()
  else if (view === 'drops') handleDiscoverDrops()
})

const emit = defineEmits(['select-reader', 'request-auth', 'drop-detail-changed', 'meet-reader', 'open-reader-detail', 'start-reading', 'open-lesson', 'ask-cards', 'show-pulse', 'view-change', 'view-deck-gallery', 'ask-mystic', 'home-changed'])

const readers = ref([])
const visibleReaderCount = ref(4)
const loadMoreEl = ref(null)
let loadMoreObserver = null
const dailyDrop = ref(null)
const dropReaderSlug = ref('iam')
const communityView = ref(props.tab === 'home' ? 'home' : 'seekers') // 'home' | 'mystics' | 'seekers' | 'pulse' (drops retired)
// Report sub-tab changes up so ReaderPage can mirror them in the URL (?tab=).
watch(communityView, (v) => emit('view-change', v))
const showImageModal = ref(false)
const expandedSign = ref(null)
const showFullSign = ref(false)
const selectedSign = ref(null)
const showSignDropdown = ref(false)

const zodiacOrder = [
  { key: 'aries', short: 'Ari', glyph: '♈', dates: 'Mar 21 – Apr 19' },
  { key: 'taurus', short: 'Tau', glyph: '♉', dates: 'Apr 20 – May 20' },
  { key: 'gemini', short: 'Gem', glyph: '♊', dates: 'May 21 – Jun 20' },
  { key: 'cancer', short: 'Can', glyph: '♋', dates: 'Jun 21 – Jul 22' },
  { key: 'leo', short: 'Leo', glyph: '♌', dates: 'Jul 23 – Aug 22' },
  { key: 'virgo', short: 'Vir', glyph: '♍', dates: 'Aug 23 – Sep 22' },
  { key: 'libra', short: 'Lib', glyph: '♎', dates: 'Sep 23 – Oct 22' },
  { key: 'scorpio', short: 'Sco', glyph: '♏', dates: 'Oct 23 – Nov 21' },
  { key: 'sagittarius', short: 'Sag', glyph: '♐', dates: 'Nov 22 – Dec 21' },
  { key: 'capricorn', short: 'Cap', glyph: '♑', dates: 'Dec 22 – Jan 19' },
  { key: 'aquarius', short: 'Aqu', glyph: '♒', dates: 'Jan 20 – Feb 18' },
  { key: 'pisces', short: 'Pis', glyph: '♓', dates: 'Feb 19 – Mar 20' },
]

function zodiacDates(key) {
  return zodiacOrder.find(z => z.key === key)?.dates || ''
}

const querentSign = computed(() => {
  const bd = store.querent_birthdate
  if (!bd) return null
  // eslint-disable-next-line no-unused-vars
  const [year, month, day] = bd.split('-').map(Number)
  if (!month || !day) return null
  const signs = [
    ['capricorn', 1, 19], ['aquarius', 2, 18], ['pisces', 3, 20],
    ['aries', 4, 19], ['taurus', 5, 20], ['gemini', 6, 20],
    ['cancer', 7, 22], ['leo', 8, 22], ['virgo', 9, 22],
    ['libra', 10, 22], ['scorpio', 11, 21], ['sagittarius', 12, 21],
    ['capricorn', 12, 31],
  ]
  for (let i = 0; i < signs.length - 1; i++) {
    const [sign, endMonth, endDay] = signs[i]
    if ((month === endMonth && day <= endDay) || month < endMonth) return sign
  }
  return 'capricorn'
})

const { loading: dropLoading, failed: dropFailed, run: runDrop } = useLoadState()
function fetchDailyDrop(readerSlug = 'iam') {
  return runDrop(() => fetchDailyDropRaw(readerSlug))
}
async function fetchDailyDropRaw(readerSlug) {
  try {
    const today = new Date()
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
    const res = await fetch(`${static_store.apiUrl}/horoscope/mystic-drop/?reader_id=${readerSlug}&date=${dateStr}`)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    dailyDrop.value = data.drops?.daily || null
    dropReaderSlug.value = readerSlug
    return true
  } catch (err) {
    console.error('Error fetching daily drop:', err)
    return null
  }
}

// eslint-disable-next-line no-unused-vars
async function loadReaderDrop(reader) {
  await fetchDailyDrop(reader.sub_domain)
  communityView.value = 'drops'
  selectedSign.value = querentSign.value || null
  showSignDropdown.value = false
  emit('meet-reader', reader)
}

async function returnToAidaDrop() {
  await fetchDailyDrop('iam')
  emit('meet-reader', null)
}

const dropReaders = computed(() => {
  return readers.value.filter(r => r.sub_domain !== dropReaderSlug.value)
})

const visibleReaders = computed(() => {
  return dropReaders.value.slice(0, visibleReaderCount.value)
})

const hasMoreReaders = computed(() => {
  return visibleReaderCount.value < dropReaders.value.length
})

// eslint-disable-next-line no-unused-vars
const questProgress = computed(() => {
  if (!props.activeQuest) return 0
  const { participant_count, threshold } = props.activeQuest
  if (!threshold) return 0
  return Math.min(100, Math.round((participant_count / threshold) * 100))
})

// eslint-disable-next-line no-unused-vars
const questTimeRemaining = computed(() => {
  if (!props.activeQuest?.expires_at) return ''
  const diff = new Date(props.activeQuest.expires_at) - new Date()
  if (diff <= 0) return 'Closed'
  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  if (days > 0) return `${days}d ${hours}h left`
  return `${hours}h left`
})


// eslint-disable-next-line no-unused-vars
function handleQuestClick() {
  if (!store.querent_id) {
    store.pendingCommunityIntent = { type: 'quest' }
    store.login_current_tab = 'register'
    emit('request-auth')
  }
}


function handleDiscoverDrops() {
  visibleReaderCount.value = 4
  communityView.value = 'drops'
  if (!readers.value.length) fetchReaders()
}

// The Mystics tab (MysticsTab.vue) loads itself.
function goHome() {
  communityView.value = 'home'
}

// The Lessons tab loads itself (LessonsTab.vue).
function handleSeekerFeed() {
  communityView.value = 'seekers'
}

const { run: runReaders } = useLoadState()   // the drops view's readers
async function fetchReaders() {
  const list = await runReaders(async () => {
    const res = await fetch(`${static_store.apiUrl}/reader/get-community-readers/`)
    if (!res.ok) throw new Error(`HTTP ${res.status}`)
    const data = await res.json()
    if (!Array.isArray(data)) throw new Error('Unexpected readers payload')
    return data
  })
  if (!list) return
  readers.value = list
}

watch(expandedSign, () => { showFullSign.value = false })
watch(communityView, (val) => { emit('drop-detail-changed', val === 'drops' ? dailyDrop.value : null) })
watch(dailyDrop, (val) => { if (communityView.value === 'drops') emit('drop-detail-changed', val) })

onMounted(() => {
  fetchDailyDrop()

  // Restore the sub-tab from a deep-link / refresh. The watch above only fires
  // when initialView *changes*; on a fresh mount the prop is already set, so
  // apply it here too. (Lessons is the default; Mystics must be asked for.)
  if (props.initialView === 'seekers') handleSeekerFeed()
  else if (props.initialView === 'mystics') goHome()
  else if (props.initialView === 'drops') handleDiscoverDrops()

  loadMoreObserver = new IntersectionObserver((entries) => {
    if (entries[0].isIntersecting && hasMoreReaders.value) {
      visibleReaderCount.value += 4
    }
  }, { threshold: 0.1 })

})

watch(loadMoreEl, (el) => {
  if (el && loadMoreObserver) loadMoreObserver.observe(el)
}, { flush: 'post' })


onUnmounted(() => {
  if (loadMoreObserver) loadMoreObserver.disconnect()
})
</script>

<style scoped>
.community-wrap { padding: 0; color: #E8D8FF; font-family: 'Inter', sans-serif; position: relative; }
.community-tabs { display: flex; justify-content: center; gap: clamp(12px, 6vw, 72px); border-bottom: 1px solid rgba(93,59,140,0.25); padding: 8px 16px 0; background: rgba(10,0,21,0.9); position: absolute; top: 0; left: 0; right: 0; z-index: 5; backdrop-filter: blur(8px); }
.community-tab { flex: 0 0 auto; padding: 8px 18px 10px; background: none; border: none; border-bottom: 2px solid transparent; color: rgba(195,169,255,0.4); font-size: 14px; font-family: 'Cinzel', serif; cursor: pointer; text-align: center; white-space: nowrap; transition: all 0.2s; }
.community-tab { position: relative; }
/* A 44px touch target past the 39px tab and its underline (e2e round 2). */
.community-tab::after { content: ''; position: absolute; inset: -5px 0; }
.community-tab:hover { color: rgba(195,169,255,0.7); }
.community-tab.active { color: #FFDFA3; border-bottom-color: #FFDFA3; }
.community-tab-home { flex: 0 0 36px; font-size: 16px; }

/* Phone, logged out: TopRightProfile's "Reveal Myself" pill is position:fixed at
   top:0/right:0 with z-index 1000, so it lands on top of this bar. At phone
   widths the centred tabs reach far enough right that the pill fully covers the
   third tab ("Voice") — not just ugly, it makes that tab unclickable.

   Reserve a band for the pill: pad the tabs bar down by its 44px height (the
   bar's own backdrop then sits behind the pill, so it reads as one header) and
   grow each view's tab clearance by the same 44px. Only applies logged out —
   when signed in the pill is gone and the layout is unchanged. Breakpoint
   matches TopRightProfile's own 560px phone rule. */
@media screen and (max-width: 560px) {
  /* Signed IN the "Reveal Myself" pill is gone, but the account portrait
     (top-right) is not. The tab row is centred across the full width, so it
     slid under the portrait and hid "Voice". Clear the header in EVERY state,
     not just logged out — the original rule assumed the pill was the only
     thing up there. The row starts below the portrait (--tra-clear-top, App.vue).
     Content clearances grow by the same amount so the absolute tab bar does
     not overlap what is beneath it. */
  .community-tabs { padding-top: var(--tra-clear-top, 60px); }

  /* More prominent: a real 44px tap target and readable at arm's length. */
  .community-tab { min-height: 44px; display: flex; align-items: center;
                   justify-content: center; font-size: 15px; padding: 8px 14px 10px; }

  .community-wrap.cta-band .community-tabs { padding-top: 52px; }
  .community-wrap.cta-band .discover-tabs-spacer { height: 104px; }
  .community-wrap.cta-band .lessons-pane { padding-top: 104px; }
}
.fold-frame { display: flex; flex-direction: column; height: calc(100vh - 80px); }

/* Mystic Drop */
.daily-drop-module { border-bottom: 1px solid rgba(93,59,140,0.25); flex: 0 0 33%; min-height: 0; overflow: hidden; }

/* Hero */
.dd-hero { position: relative; width: 100%; height: 100%; overflow: hidden; cursor: pointer; }
.dd-hero-img { width: 100%; height: 100%; object-fit: cover; object-position: center center; display: block; }
.dd-hero-overlay { position: absolute; inset: 0; background: linear-gradient(to top, rgba(13,5,32,0.85) 0%, rgba(13,5,32,0.45) 40%, rgba(13,5,32,0.15) 100%); display: flex; flex-direction: column; justify-content: center; align-items: center; gap: 4px; padding: 40px 16px 10px; }
.dd-date-header { position: absolute; top: 60px; left: 16px; font-size: 14px; font-weight: 500; color: rgba(195,169,255,0.7); text-align: left; letter-spacing: 0.05em; text-shadow: 0 1px 4px rgba(0,0,0,0.5); line-height: 1.4; }
.dd-hero-center { width: 100%; }
.ma-avatar { width: 28px; height: 28px; border-radius: 50%; overflow: hidden; border: 1px solid rgba(195,169,255,0.4); flex-shrink: 0; }
.ma-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.ma-avatar-placeholder { width: 28px; height: 28px; border-radius: 50%; background: rgba(93,59,140,0.5); border: 1px solid rgba(195,169,255,0.3); flex-shrink: 0; }
.ma-name { font-size: 11px; font-weight: 600; color: #C3A9FF; display: block; line-height: 1.2; }
.dd-badge-label { font-size: 9px; color: #FFDFA3; letter-spacing: 0.06em; text-transform: uppercase; }
.dd-hero-title { display: block; margin-bottom: 30px; color: #C3A9FF; font-size: 16px; font-weight: 600; text-align: center; font-family: 'Cinzel', serif; letter-spacing: 0.03em; text-shadow: 0 1px 4px rgba(0,0,0,0.6); }
.dd-hero-teaser-group { display: flex; flex-direction: column; align-items: center; background: rgba(13,5,32,0.55); border-radius: 12px; padding: 14px; margin-right: 185px; }
.dd-explore-btn { position: absolute; bottom: 8px; left: calc(50% - 92px); transform: translateX(-50%); padding: 5px 16px; background: rgba(93,59,140,0.35); border: 1px solid rgba(93,59,140,0.6); border-radius: 6px; color: #C3A9FF; font-size: 11px; font-weight: 500; cursor: pointer; font-family: inherit; letter-spacing: 0.03em; transition: background 0.2s; }
.dd-explore-btn:hover { background: rgba(93,59,140,0.55); }
.dd-hero-teaser { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 16px; color: #FFDFA3; line-height: 1.5; margin: 0; font-style: italic; text-align: center; text-shadow: 0 1px 4px rgba(0,0,0,0.4); }
.dd-hero-tarot { position: absolute; top: calc(50% + 30px); right: 16px; transform: translateY(-50%); display: flex; flex-direction: column; align-items: center; gap: 0; }
.dd-hero-tarot-img { width: 120px; height: auto; object-fit: contain; display: block; border-radius: 8px; border: 2px solid rgba(195,169,255,0.25); box-shadow: 0 4px 16px rgba(0,0,0,0.6); margin-bottom: 10px; }
.dd-hero-tarot-name { font-size: 10px; color: rgba(195,169,255,0.6); text-align: center; max-width: 100px; line-height: 1.2; }

/* Body — expandable reading */
.dd-body { padding: 10px 16px; }
.dd-reading { font-size: 12px; color: rgba(255,255,255,0.45); line-height: 1.6; margin: 0 0 6px; white-space: pre-wrap; }
.dd-read-toggle { background: none; border: none; color: #C3A9FF; font-size: 11px; cursor: pointer; padding: 0; font-family: inherit; }
.dd-read-toggle:hover { text-decoration: underline; }

/* Zodiac strip */
.dd-signs { display: flex; gap: 2px; padding: 0 12px 10px; overflow-x: auto; scrollbar-width: none; -webkit-overflow-scrolling: touch; }
.dd-signs::-webkit-scrollbar { display: none; }
.dd-sign-chip { display: flex; flex-direction: column; align-items: center; gap: 3px; padding: 6px 4px; border-radius: 8px; cursor: pointer; flex-shrink: 0; min-width: 38px; transition: background 0.2s, transform 0.15s; }
.dd-sign-chip:hover { background: rgba(93,59,140,0.2); }
.dd-sign-chip.active { background: rgba(195,169,255,0.12); transform: translateY(-1px); }
.dd-sign-icon { width: 28px; height: 28px; border-radius: 50%; object-fit: cover; border: 1px solid rgba(195,169,255,0.15); }
.dd-sign-chip.active .dd-sign-icon { border-color: rgba(195,169,255,0.5); }
.dd-sign-label { font-size: 8px; color: rgba(195,169,255,0.4); letter-spacing: 0.03em; }
.dd-sign-chip.active .dd-sign-label { color: #C3A9FF; }

/* Sign reading expansion */
.dd-sign-reading { padding: 0 16px 14px; }
.dd-sign-reading-header { display: flex; align-items: center; gap: 10px; margin-bottom: 8px; }
.dd-sign-reading-img { width: 40px; height: 40px; border-radius: 50%; object-fit: cover; border: 1px solid rgba(195,169,255,0.25); }
.dd-sign-reading-name { font-size: 13px; font-weight: 500; color: #E8D8FF; display: block; line-height: 1.2; }
.dd-sign-reading-dates { font-size: 10px; color: rgba(195,169,255,0.4); }
.dd-sign-reading-text { font-size: 12px; color: rgba(255,255,255,0.4); line-height: 1.65; margin: 0 0 6px; white-space: pre-wrap; }

/* Date footer */
.dd-date { padding: 6px 16px 12px; font-size: 9px; color: rgba(195,169,255,0.3); letter-spacing: 0.05em; text-transform: uppercase; }

/* Transition */
.sign-fade-enter-active, .sign-fade-leave-active { transition: opacity 0.2s, transform 0.2s; }
.sign-fade-enter-from, .sign-fade-leave-to { opacity: 0; transform: translateY(-4px); }

/* Loading */
.dd-loading { padding: 24px 16px; }
.dd-loading-inner { display: flex; align-items: center; gap: 8px; }
.dd-loading-text { font-style: italic; color: rgba(255,255,255,0.25); font-size: 12px; }
.presence-pulse { width: 7px; height: 7px; border-radius: 50%; background: #663399; animation: pulse 2s ease-in-out infinite; flex-shrink: 0; }
@keyframes pulse { 0%,100%{opacity:0.4;transform:scale(1)} 50%{opacity:1;transform:scale(1.4)} }

/* Carousel */
.carousel-section { padding: 14px 16px; border-bottom: 1px solid rgba(255,255,255,0.05); }
.section-label { display: block; font-size: 10px; font-weight: 500; color: rgba(255,255,255,0.25); letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 10px; text-align: center; }
.carousel-header { margin-bottom: 10px; }
.carousel-controls { display: flex; gap: 8px; align-items: center; }
.search-wrap { flex: 1 1 0; min-width: 0; overflow: hidden; }
.reader-search { width: 100%; padding: 6px 10px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.1); border-radius: 8px; color: #E8D8FF; font-size: 11px; outline: none; box-sizing: border-box; }
.reader-search::placeholder { color: rgba(255,255,255,0.25); }
.filter-chips { display: flex; gap: 4px; flex-shrink: 0; }
.filter-chip { padding: 4px 8px; background: transparent; border: 1px solid rgba(255,255,255,0.1); border-radius: 14px; font-size: 10px; color: rgba(255,255,255,0.35); cursor: pointer; white-space: nowrap; }
.filter-chip.active, .filter-chip:hover { background: rgba(93,59,140,0.3); border-color: rgba(195,169,255,0.35); color: #C3A9FF; }
.reader-carousel { display: flex; gap: 10px; overflow-x: auto; padding-bottom: 4px; scrollbar-width: none; -webkit-overflow-scrolling: touch; }
.reader-carousel::-webkit-scrollbar { display: none; }
.carousel-card { flex-shrink: 0; width: 140px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07); border-radius: 12px; overflow: hidden; cursor: pointer; transition: border-color 0.2s, transform 0.15s; }
.carousel-card:hover { border-color: rgba(195,169,255,0.3); transform: translateY(-2px); }
.carousel-card-img { position: relative; height: 120px; background: rgba(93,59,140,0.25); overflow: hidden; }
.carousel-card-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
.carousel-card-placeholder { width: 100%; height: 100%; background: rgba(93,59,140,0.2); }
.carousel-modality-badge { position: absolute; bottom: 5px; left: 6px; font-size: 9px; padding: 2px 5px; background: rgba(18,8,42,0.85); border: 1px solid rgba(195,169,255,0.2); border-radius: 6px; color: rgba(195,169,255,0.75); max-width: 120px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.carousel-card-body { padding: 8px 10px 10px; }
.carousel-card-name { font-size: 11px; font-weight: 500; color: #E8D8FF; margin-bottom: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.carousel-card-by { font-size: 9px; color: rgba(255,255,255,0.2); margin-bottom: 6px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.carousel-chat-btn { width: 100%; padding: 5px; background: rgba(93,59,140,0.3); border: 1px solid rgba(93,59,140,0.5); border-radius: 6px; color: #C3A9FF; font-size: 10px; cursor: pointer; text-align: center; }
.carousel-empty { flex-shrink: 0; padding: 20px; font-size: 12px; color: rgba(255,255,255,0.25); font-style: italic; }

/* Quest */
.quest-module { background: rgba(50,192,158,0.05); border-bottom: 1px solid rgba(50,192,158,0.15); padding: 14px 16px; cursor: pointer; }
.quest-top { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.quest-badge { font-size: 10px; padding: 2px 8px; background: rgba(50,192,158,0.1); border: 1px solid rgba(50,192,158,0.25); border-radius: 6px; color: #32C09E; }
.quest-timer { font-size: 10px; color: rgba(255,255,255,0.3); }
.quest-title { font-size: 14px; font-weight: 500; color: #7DE8CC; margin-bottom: 5px; }
.quest-desc { font-size: 11px; color: rgba(255,255,255,0.4); line-height: 1.55; margin-bottom: 10px; }
.quest-bar { background: rgba(255,255,255,0.07); border-radius: 3px; height: 4px; overflow: hidden; margin-bottom: 5px; }
.quest-fill { background: #32C09E; height: 100%; border-radius: 3px; transition: width 0.6s ease; }
.quest-meta-row { display: flex; justify-content: space-between; margin-bottom: 8px; }
.quest-meta { font-size: 10px; color: rgba(255,255,255,0.25); }
.quest-rewards { display: flex; gap: 6px; margin-bottom: 10px; }
.reward-pill { display: inline-flex; align-items: center; gap: 4px; padding: 3px 8px; border-radius: 20px; font-size: 10px; }
.reward-emerald { background: rgba(50,192,158,0.1); border: 1px solid rgba(50,192,158,0.25); color: #32C09E; }
.quest-cta { width: 100%; padding: 8px; background: rgba(50,192,158,0.1); border: 1px solid rgba(50,192,158,0.3); border-radius: 8px; color: #7DE8CC; font-size: 11px; font-weight: 500; cursor: pointer; text-align: center; font-family: inherit; }
.quest-completed { font-size: 11px; color: rgba(50,192,158,0.6); text-align: center; padding: 6px 0; }

/* Resonating — Community Pulse Drop */
.vibes-section { padding: 14px 16px; border-bottom: 1px solid rgba(255,255,255,0.05); }
.vibes-header { display: flex; align-items: center; gap: 10px; margin-bottom: 12px; }
.vibes-card-badge { display: flex; flex-direction: column; align-items: center; gap: 3px; flex-shrink: 0; width: 50px; }
.vibes-card-img { width: 44px; height: 66px; object-fit: cover; border-radius: 4px; border: 1px solid rgba(195,169,255,0.2); display: block; }
.vibes-card-label { font-size: 8px; color: rgba(195,169,255,0.35); text-align: center; line-height: 1.2; }
.vibes-title { font-size: 14px; font-weight: 500; color: #FFDFA3; margin-bottom: 3px; }
.vibes-teaser { font-size: 11px; color: rgba(255,255,255,0.35); line-height: 1.5; }
.vibes-list { display: flex; flex-direction: column; gap: 8px; }
.vibe-item { display: flex; align-items: flex-start; gap: 8px; padding: 10px 12px; background: rgba(93,59,140,0.08); border-left: 2px solid rgba(195,169,255,0.2); border-radius: 0 8px 8px 0; }
.vibe-dot { width: 6px; height: 6px; border-radius: 50%; background: rgba(195,169,255,0.4); margin-top: 6px; flex-shrink: 0; }
.vibe-content { flex: 1; min-width: 0; }
.vibe-title { display: block; font-size: 12px; font-weight: 500; color: rgba(195,169,255,0.8); font-style: italic; margin-bottom: 3px; }
.vibe-text { font-size: 11px; color: rgba(255,255,255,0.35); line-height: 1.55; margin: 0; }

/* Circle feed */
/* Spotlight row — side by side */
.spotlight-row { display: flex; gap: 10px; padding: 10px 16px; flex: 1; min-height: 0; overflow: visible; align-items: stretch; }
.spotlight-col { flex: 1; min-width: 0; display: flex; flex-direction: column; }

/* Curated reader spotlight */
.spotlight-card { flex: 1; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07); border-radius: 12px; overflow: hidden; cursor: pointer; transition: border-color 0.2s, transform 0.15s; display: flex; flex-direction: column; }
.spotlight-card:hover { border-color: rgba(195,169,255,0.3); transform: translateY(-2px); }
.spotlight-card-hero { display: flex; flex-direction: column; align-items: center; padding: 14px 10px 12px; }
.spotlight-avatar { width: 140px; height: 140px; border-radius: 50%; overflow: hidden; border: 2px solid rgba(195,169,255,0.5); box-shadow: 0 2px 12px rgba(0,0,0,0.5); }
.spotlight-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.spotlight-card-name { font-family: 'Cinzel', serif; font-size: 15px; font-weight: 600; color: #FFDFA3; margin: 0 0 10px; text-align: center; line-height: 1.3; }
.spotlight-card-stats { display: flex; flex-wrap: wrap; gap: 6px; padding: 12px 12px; }
.spotlight-stat { font-size: 12px; padding: 3px 10px; background: rgba(93,59,140,0.15); border: 1px solid rgba(93,59,140,0.25); border-radius: 4px; color: rgba(195,169,255,0.6); }
.spotlight-card-teaser { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 14px; color: rgba(255,255,255,0.45); font-style: italic; line-height: 1.6; margin: 0; padding: 12px 12px 20px; flex: 1; }
.spotlight-card-btn-wrap { padding: 0 12px 24px; }
.spotlight-card-btn { width: 100%; padding: 10px 10px; background: rgba(93,59,140,0.3); border: 1px solid rgba(93,59,140,0.5); border-radius: 8px; color: #C3A9FF; font-size: 13px; cursor: pointer; text-align: center; font-family: inherit; }
.explore-mystics-btn { margin-top: 16px; width: 100%; padding: 22px 12px; background: rgba(93,59,140,0.35); border: 1px solid rgba(195,169,255,0.3); border-radius: 10px; color: #FFDFA3; font-size: 17px; font-family: 'Cinzel', serif; font-weight: 500; cursor: pointer; text-align: center; letter-spacing: 0.03em; transition: background 0.2s, border-color 0.2s; }
.explore-mystics-btn:hover { background: rgba(93,59,140,0.5); border-color: rgba(195,169,255,0.5); }

/* Shared reading spotlight */
.shared-spotlight { flex: 1; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; overflow: hidden; transition: border-color 0.2s; display: flex; flex-direction: column; }
.shared-spotlight:hover { border-color: rgba(195,169,255,0.25); }
.shared-spotlight-hero { position: relative; height: 160px; overflow: hidden; cursor: pointer; }
.shared-spotlight-hero:hover { opacity: 0.9; }
.shared-spotlight-hero-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.shared-spotlight-title { position: absolute; top: 0; left: 0; right: 0; font-family: 'Cinzel', serif; font-size: 12px; color: #FFDFA3; margin: 0; line-height: 1.3; padding: 8px 10px 24px; text-align: center; background: linear-gradient(to bottom, rgba(10,0,21,0.85) 0%, rgba(10,0,21,0.4) 60%, transparent 100%); }
.shared-spotlight-byline { display: flex; justify-content: space-between; align-items: center; padding: 6px 10px; }
.shared-spotlight-by { font-size: 10px; color: rgba(255,255,255,0.3); }
.shared-spotlight-reader { font-size: 10px; color: rgba(195,169,255,0.6); cursor: pointer; text-decoration: underline; text-decoration-color: rgba(195,169,255,0.3); }
.shared-spotlight-reader:hover { color: #C3A9FF; text-decoration-color: #C3A9FF; }
.shared-spotlight-views { font-size: 10px; color: rgba(255,255,255,0.25); }
.shared-spotlight-querent { font-family: 'Cinzel', serif; font-size: 15px; font-weight: 600; color: #FFDFA3; margin: 0; padding: 14px 10px 0; text-align: center; }
.shared-spotlight-avatar { display: flex; justify-content: center; padding: 10px 0; }
.shared-spotlight-avatar img { width: 140px; height: 140px; border-radius: 50%; object-fit: cover; border: 2px solid rgba(195,169,255,0.4); box-shadow: 0 2px 10px rgba(0,0,0,0.4); }
.shared-spotlight-label { display: block; font-size: 10px; color: rgba(255,255,255,0.25); letter-spacing: 0.06em; text-transform: uppercase; text-align: center; padding: 4px 10px 6px; }
.shared-spotlight-cards { display: flex; gap: 8px; padding: 10px 10px 8px; justify-content: center; }
.shared-tarot-card-wrap { display: flex; flex-direction: column; align-items: center; gap: 3px; flex: 1; min-width: 0; }
.shared-tarot-card { width: 100%; aspect-ratio: 2/3; border-radius: 5px; overflow: hidden; border: 1px solid rgba(195,169,255,0.15); }
.shared-tarot-label { font-size: 8px; color: rgba(195,169,255,0.4); text-align: center; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
.shared-tarot-card.reversed { transform: rotate(180deg); }
.shared-tarot-img { width: 100%; height: 100%; object-fit: cover; display: block; }
.shared-tarot-placeholder { width: 100%; height: 100%; background: rgba(93,59,140,0.3); }
.shared-spotlight-btn-wrap { margin-top: auto; padding: 0 12px 24px; }
.shared-spotlight-btn { width: 100%; padding: 10px; background: rgba(93,59,140,0.3); border: 1px solid rgba(93,59,140,0.5); border-radius: 8px; color: #C3A9FF; font-size: 13px; cursor: pointer; text-align: center; font-family: inherit; }
.meet-seekers-btn { margin-top: 16px; width: 100%; padding: 22px 12px; background: rgba(93,59,140,0.35); border: 1px solid rgba(195,169,255,0.3); border-radius: 10px; color: #FFDFA3; font-size: 17px; font-family: 'Cinzel', serif; font-weight: 500; cursor: pointer; text-align: center; letter-spacing: 0.03em; transition: background 0.2s, border-color 0.2s; }
.meet-seekers-btn:hover { background: rgba(93,59,140,0.5); border-color: rgba(195,169,255,0.5); }
.drop-card { background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.07); border-radius: 12px; overflow: hidden; cursor: pointer; transition: border-color 0.2s; }
.drop-card:hover { border-color: rgba(195,169,255,0.25); }
.drop-card-inner { display: flex; }
.drop-img { width: 80px; height: 90px; flex-shrink: 0; background: rgba(93,59,140,0.25); overflow: hidden; }
.drop-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
.drop-img-placeholder { width: 100%; height: 100%; background: rgba(93,59,140,0.2); }
.drop-content { flex: 1; padding: 10px 12px; min-width: 0; }
.drop-meta { display: flex; align-items: center; gap: 5px; margin-bottom: 4px; }
.drop-reader { font-size: 10px; color: rgba(195,169,255,0.6); font-weight: 500; }
.drop-type-badge { font-size: 9px; padding: 1px 5px; background: rgba(93,59,140,0.2); border: 1px solid rgba(93,59,140,0.3); border-radius: 4px; color: rgba(195,169,255,0.4); }
.drop-title { font-size: 12px; font-weight: 500; color: #E8D8FF; margin-bottom: 3px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.drop-teaser { font-size: 11px; color: rgba(255,255,255,0.3); line-height: 1.45; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.drop-card-name { font-size: 9px; color: rgba(195,169,255,0.3); margin-top: 5px; }
.offering-card { background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; overflow: hidden; cursor: pointer; transition: border-color 0.2s; }
.offering-card:hover { border-color: rgba(195,169,255,0.2); }
.offering-img { width: 100%; height: 110px; object-fit: cover; display: block; }
.offering-body { padding: 10px 12px; }
.offering-header { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; }
.offering-avatar { width: 18px; height: 18px; border-radius: 50%; background: rgba(195,169,255,0.1); border: 1px solid rgba(195,169,255,0.15); flex-shrink: 0; }
.offering-name { font-size: 10px; color: rgba(255,255,255,0.3); }
.offering-action { font-size: 10px; color: rgba(255,255,255,0.15); }
.offering-question { font-size: 12px; color: rgba(255,255,255,0.4); line-height: 1.5; margin-bottom: 4px; }
.offering-theme { font-size: 11px; color: rgba(195,169,255,0.4); font-style: italic; margin-bottom: 8px; }
.offering-footer { display: flex; align-items: center; justify-content: space-between; }
.offering-cards { display: flex; gap: 4px; }
.mini-card { width: 22px; height: 34px; background: rgba(93,59,140,0.3); border-radius: 3px; border: 1px solid rgba(195,169,255,0.12); }
.offering-stats { display: flex; gap: 10px; }
.stat { font-size: 10px; color: rgba(255,255,255,0.2); }

/* ── Drop Detail — Discover Mystics ── */
.drop-detail-mystics { margin-top: 8px; padding-bottom: 80px; }
.drop-detail-mystics-title { font-family: 'Cinzel', serif; font-size: 16px; font-weight: 500; color: #FFDFA3; text-align: center; margin: 16px 0 12px; letter-spacing: 0.03em; }
.drop-detail-reader-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 8px; }
.drop-detail-reader-card { display: flex; flex-direction: column; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.07); border-radius: 12px; overflow: hidden; cursor: pointer; transition: border-color 0.2s, transform 0.15s; }
.drop-detail-reader-card:hover { border-color: rgba(195,169,255,0.3); transform: translateY(-2px); }
.drop-detail-reader-img { position: relative; flex: 1; min-height: 180px; background: rgba(93,59,140,0.25); overflow: hidden; }
.drop-detail-reader-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
.drop-detail-reader-placeholder { width: 100%; height: 100%; background: rgba(93,59,140,0.2); }
.drop-detail-reader-name { position: absolute; top: 0; left: 0; right: 0; z-index: 1; font-family: 'Cinzel', serif; font-size: 15px; font-weight: 500; color: #FFDFA3; margin: 0; line-height: 1.3; text-align: center; padding: 8px 10px 24px; background: linear-gradient(to bottom, rgba(10,0,21,0.85) 0%, rgba(10,0,21,0.4) 60%, transparent 100%); }
.drop-detail-reader-btn-wrap { padding: 8px 10px; }
.drop-detail-reader-btn { width: 100%; padding: 6px; background: rgba(93,59,140,0.3); border: 1px solid rgba(93,59,140,0.5); border-radius: 6px; color: #C3A9FF; font-size: 10px; cursor: pointer; text-align: center; font-family: inherit; }
.drop-detail-load-more { text-align: center; padding: 12px; font-size: 11px; color: rgba(195,169,255,0.4); cursor: pointer; font-style: italic; transition: color 0.2s; }
.drop-detail-load-more:hover { color: #C3A9FF; }
.drop-detail-end { cursor: default; color: rgba(195,169,255,0.25); }
.drop-detail-end:hover { color: rgba(195,169,255,0.25); }

/* ── Drop Detail Page ── */
.drop-detail { padding: 0; color: #E8D8FF; min-height: 100%; background: #0a0015; }
.drop-detail-back { background: none; border: none; color: rgba(195,169,255,0.5); font-size: 12px; cursor: pointer; padding: 44px 16px 12px; font-family: inherit; }
.drop-detail-back:hover { color: #C3A9FF; }
.drop-detail-body { padding: 54px 20px 10px; position: relative; }
.drop-detail-date { display: block; font-size: 10px; color: rgba(195,169,255,0.5); letter-spacing: 0.05em; text-transform: uppercase; margin-bottom: 10px; }

.drop-detail-top { display: flex; gap: 16px; padding-bottom: 13px; border-bottom: 1px solid rgba(93,59,140,0.25); margin-left: -20px; margin-right: -20px; padding-left: 20px; padding-right: 20px; height: calc((100vh - 80px) * 0.33 - 7px - 60px); overflow: hidden; }
.drop-detail-img-wrap { flex-shrink: 0; display: flex; align-items: center; cursor: pointer; }
.drop-detail-img-wrap:hover { opacity: 0.9; }
.drop-detail-hero-img { height: 140px; width: auto; display: block; border-radius: 8px; border: 2px solid rgba(195,169,255,0.25); box-shadow: 0 4px 16px rgba(0,0,0,0.6); }
.drop-detail-card-name { display: block; font-family: 'Cinzel', serif; font-size: 14px; font-weight: 500; color: #FFDFA3; margin-bottom: 2px; }
.drop-detail-info-tip { display: inline-flex; align-items: center; justify-content: center; width: 14px; height: 14px; border-radius: 50%; background: rgba(195,169,255,0.15); border: 1px solid rgba(195,169,255,0.3); color: rgba(195,169,255,0.6); font-size: 9px; font-family: 'Inter', sans-serif; font-weight: 600; cursor: help; margin-left: 4px; vertical-align: middle; position: relative; }
.drop-detail-card-date { display: block; font-size: 11px; color: rgba(195,169,255,0.5); margin-bottom: 10px; }
.drop-detail-text { flex: 1; min-width: 0; }
.drop-detail-title { font-family: 'Cinzel', serif; font-size: 20px; font-weight: 500; color: #FFDFA3; margin: 0 0 10px; line-height: 1.3; }
.drop-detail-teaser { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 16px; color: #FFDFA3; font-style: italic; line-height: 1.6; margin: 0 0 10px; }
.drop-detail-reading { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 14px; color: rgba(255,255,255,0.45); line-height: 1.6; margin: 20px 0 0; white-space: pre-wrap; text-align: justify; }

/* Fullscreen image modal */
.image-modal-overlay { position: fixed; inset: 0; z-index: 9999; background: rgba(0,0,0,0.92); display: flex; align-items: center; justify-content: center; cursor: pointer; padding: 20px; }
.image-modal-img { max-width: 100%; max-height: 100%; object-fit: contain; border-radius: 12px; }

/* Zodiac signs accordion */
.drop-detail-signs { margin-top: 8px; }
.drop-detail-signs-label { display: block; font-size: 10px; font-weight: 500; color: rgba(255,255,255,0.25); letter-spacing: 0.08em; text-transform: uppercase; margin-bottom: 12px; }
/* Sign picker grid (no sign selected) */
.sign-picker-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
.sign-picker-item { display: flex; flex-direction: column; align-items: center; gap: 4px; padding: 10px 4px; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06); border-radius: 10px; cursor: pointer; transition: border-color 0.2s, transform 0.15s; }
.sign-picker-item:hover { border-color: rgba(195,169,255,0.3); transform: translateY(-2px); }
.sign-picker-img { width: 44px; height: 44px; border-radius: 50%; object-fit: cover; border: 1px solid rgba(195,169,255,0.2); }
.sign-picker-placeholder { width: 44px; height: 44px; border-radius: 50%; background: rgba(93,59,140,0.25); border: 1px solid rgba(195,169,255,0.15); display: flex; align-items: center; justify-content: center; font-size: 20px; color: rgba(195,169,255,0.5); }
.sign-picker-name { font-family: 'Cinzel', serif; font-size: 10px; color: #E8D8FF; text-align: center; line-height: 1.2; }
.sign-picker-dates { font-size: 8px; color: rgba(195,169,255,0.35); text-align: center; }

/* Sign reading (sign selected) */
.sign-reading { position: relative; }
.sign-reading-selector { display: flex; align-items: center; gap: 10px; padding: 10px 14px; background: rgba(255,255,255,0.03); border: 1px solid rgba(195,169,255,0.2); border-radius: 10px; cursor: pointer; margin-bottom: 12px; transition: border-color 0.2s; }
.sign-reading-selector:hover { border-color: rgba(195,169,255,0.4); }
.sign-reading-current-img { width: 40px; height: 40px; border-radius: 50%; object-fit: cover; border: 1px solid rgba(195,169,255,0.25); }
.sign-reading-current-info { flex: 1; }
.sign-reading-current-name { font-family: 'Cinzel', serif; font-size: 15px; font-weight: 500; color: #FFDFA3; display: block; }
.sign-reading-current-dates { font-size: 10px; color: rgba(195,169,255,0.4); }
.sign-reading-chevron { font-size: 10px; color: rgba(195,169,255,0.4); }

/* Dropdown */
.sign-dropdown { position: absolute; top: 60px; left: 0; right: 0; z-index: 10; background: #0d0520; border: 1px solid rgba(195,169,255,0.2); border-radius: 10px; max-height: 300px; overflow-y: auto; scrollbar-width: thin; scrollbar-color: rgba(102,51,153,0.3) transparent; }
.sign-dropdown-item { display: flex; align-items: center; gap: 8px; padding: 8px 14px; cursor: pointer; transition: background 0.15s; }
.sign-dropdown-item:hover { background: rgba(93,59,140,0.15); }
.sign-dropdown-item.active { background: rgba(255,223,163,0.06); }
.sign-dropdown-img { width: 28px; height: 28px; border-radius: 50%; object-fit: cover; border: 1px solid rgba(195,169,255,0.15); }
.sign-dropdown-name { font-family: 'Cinzel', serif; font-size: 12px; color: #E8D8FF; flex: 1; }
.sign-dropdown-dates { font-size: 9px; color: rgba(195,169,255,0.35); }

/* Reading text */
.sign-reading-body { padding: 4px 0; }
.sign-reading-text { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 15px; color: rgba(255,255,255,0.45); line-height: 1.7; margin: 0; white-space: pre-wrap; text-align: justify; }
.drop-detail-sign-header { display: flex; align-items: center; gap: 10px; padding: 10px 14px; cursor: pointer; transition: background 0.15s; }
.drop-detail-sign-header:hover { background: rgba(93,59,140,0.1); }
.drop-detail-sign-img { width: 36px; height: 36px; border-radius: 50%; object-fit: cover; border: 1px solid rgba(195,169,255,0.2); flex-shrink: 0; }
.drop-detail-sign-info { flex: 1; min-width: 0; }
.drop-detail-sign-name { display: block; font-size: 13px; font-weight: 500; color: #E8D8FF; line-height: 1.2; }
.drop-detail-sign-dates { font-size: 10px; color: rgba(195,169,255,0.4); }
.drop-detail-sign-toggle { font-size: 18px; color: rgba(195,169,255,0.4); flex-shrink: 0; width: 20px; text-align: center; }
.drop-detail-sign-body { padding: 0 14px 14px; }
.drop-detail-sign-body p { font-size: 13px; color: rgba(255,255,255,0.4); line-height: 1.65; margin: 0; white-space: pre-wrap; }

/* ── Mystics discovery gallery (Home) ── */
/* No top padding — a scroll-container's top padding offsets sticky children
   (would push the sticky search bar down off the tabs). The .discover-tabs-spacer
   clears the absolute tabs instead. */

.discover-tabs-spacer { height: 52px; flex: none; }

/* Featured row — Mystic + Shared Reading side by side */
/* Mirror the Fireside head row: a large hero + a slim 286px Top Mystics rail. */
/* Hero stretches to match the Top Mystics rail height (the "right set"), so both
   hero slides are the same height as the rail. */
/* Fixed row height so BOTH hero slides (Featured Mystic ~398 + Just Arrived) are
   exactly the same size — switching bullets can never resize the panel. 404 clears
   the taller (Featured Mystic) slide without clipping. Relaxed to auto on phones. */
/* Title row above both columns — one line, equal height. Left = centered hero
   title (changes with the slide); right = "Top Mystics" flanked by paging arrows. */

/* Each cell stacks its title over a bullet row. */

/* Eyebrow look — matches Fireside .discover-featured-label (Cinzel, gold, uppercase, letter-spaced). */

/* Hero title — left-aligned section label. */

/* "✦ Featured" section label — left-aligned, tucked close above the hero. */

/* Rankings-card dimension label is centered between its cycle arrows (inside the card). */

/* Mobile-only ranking header (shown ≤760, directly above the ranked cards). */

/* Mobile-only hero slide bullets (shown ≤760, directly below the hero). */

/* Position/where-am-I bullets under each title (smaller than the old ones). */

/* Bullets row BELOW the components — mirrors the two-column grid, bullets centered
   under each. */

/* ≤760 (single-column): the bottom bullets row is detached from its components —
   hide it; the hero bullets show below the hero instead (.ft-bullets-mobile).
   Placed AFTER the base .featured-bullets rule so source order wins the cascade. */

/* Sort selector now sits atop the ranked list. */
.top-sort-row { display: flex; justify-content: center; margin-bottom: 10px; }

/* Controls row mirrors the featured-row columns so the bullets center under the
   hero and the ranking arrows center under Top Mystics (symmetry). */

.fc-cell { display: flex; justify-content: center; align-items: center; min-height: 30px; }
.rank-arrows { display: flex; gap: 8px; }
.rank-arrow { width: 30px; height: 30px; border-radius: 50%; cursor: pointer; line-height: 1;
  font-size: 14px; color: #C3A9FF; background: rgba(255,255,255,0.04);
  border: 1px solid rgba(195,169,255,0.4); transition: all 0.18s ease; }
.rank-arrow:hover:not(:disabled) { border-color: rgba(255,223,163,0.6); color: #f0d9a3; }
.rank-arrow:disabled { opacity: 0.3; cursor: default; }
/* ≤760: the hero goes full-width — the side-by-side showcase still fits here,
   so keep the fixed height (both slides stay equal). */

/* True phone widths: the full-width hero can no longer fit the side-by-side
   showcase — stack it and let the height follow content. */

/* "Most Readings" hero card — ranked list centered in the hero, 2 columns on
   desktop, stacked on phones. Reuses the .top-mystic row styling. */

.discover-featured-label { display: flex; align-items: center; min-height: 30px; font-family: 'Cinzel', serif; letter-spacing: 0.2em; font-size: 11px; color: #FFDFA3; text-transform: uppercase; margin: 0 0 10px; }

.discover-featured-meta { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin: 0; }
.discover-featured-cta { margin-top: auto; width: 100%; padding: 11px; background: rgba(93,59,140,0.3); border: 1px solid rgba(195,169,255,0.35); border-radius: 10px; color: #FFDFA3; font-size: 13px; font-family: 'Cinzel', serif; font-weight: 500; cursor: pointer; text-align: center; transition: background 0.2s; }
.discover-featured-cta:hover { background: rgba(93,59,140,0.5); }

/* Top Mystics list */
.top-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; min-height: 30px; margin-bottom: 10px; }
.top-head .discover-featured-label { margin: 0; min-height: 0; }
.top-sort { display: flex; gap: 4px; }
.top-sort-btn { font-size: 11px; font-family: 'Inter', sans-serif; color: rgba(195,169,255,0.55); background: transparent; border: 1px solid rgba(255,255,255,0.1); border-radius: 999px; padding: 3px 10px; cursor: pointer; transition: all 0.15s; }
.top-sort-btn:hover { color: #C3A9FF; border-color: rgba(195,169,255,0.3); }
.top-sort-btn.active { color: #1a0f2e; background: linear-gradient(180deg, #FFDFA3, #cdae6a); border-color: transparent; font-weight: 600; }

/* Invisible sentinel at the list bottom; when it scrolls into view the next 5
   load (lazy scroll). 1px so the observer fires just before the true bottom. */
.top-sentinel { flex: none; height: 1px; }

/* Grid — image-led cards */
/* Search bar sticks just under the tabs once it scrolls up to them, so search is
   always reachable while the gallery scrolls beneath it. */

/* 2 per row (was auto-fill ~4). */

/* Collapsed: clip to the first row (overflow hidden → NO scrollbar). The next
   readers are hidden until "Reveal More" or a panel scroll expands it. */

/* Split card: avatar (with the name layer) on the LEFT, teaser + Get Reading CTA
   on the RIGHT. */

/* The name floats over the lower portion of the avatar — a dark gradient keeps
   it legible against any image. */

/* Right panel — teaser at the top, CTA pinned to the bottom. */

/* One quiet line of facts under the teaser: dot-separated, wraps on narrow cards. */

/* On phones a 2-up split is too cramped — give each split card the full width. */

/* ── Seeker Feed ── */
.seeker-feed { padding: 0; color: #E8D8FF; min-height: 100%; background: #0a0015; padding-bottom: 80px; }

/* Pinned pulse */
.feed-pinned { padding: 0 16px 16px; }
.feed-pinned-card { display: flex; gap: 12px; background: rgba(255,255,255,0.03); border: 1px solid rgba(255,255,255,0.08); border-radius: 12px; padding: 12px; }
.feed-pinned-img { width: 60px; height: 60px; border-radius: 8px; overflow: hidden; flex-shrink: 0; }
.feed-pinned-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
.feed-pinned-body { flex: 1; min-width: 0; }
.feed-pinned-badge { font-size: 9px; color: rgba(195,169,255,0.5); text-transform: uppercase; letter-spacing: 0.08em; }
.feed-pinned-title { font-family: 'Cinzel', serif; font-size: 14px; font-weight: 500; color: #FFDFA3; margin: 2px 0 2px; }
.feed-pinned-subtitle { font-size: 10px; color: rgba(195,169,255,0.4); display: block; }
.feed-pinned-teaser { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 12px; color: rgba(255,255,255,0.35); font-style: italic; margin: 4px 0 0; line-height: 1.4; }

/* Feed list */
.feed-list { padding: 0 16px; }
.feed-item { margin-bottom: 16px; background: rgba(255,255,255,0.02); border: 1px solid rgba(255,255,255,0.06); border-radius: 12px; overflow: hidden; cursor: pointer; transition: border-color 0.2s; }
.feed-item:hover { border-color: rgba(195,169,255,0.2); }

/* Feed item header */
.feed-item-header { display: flex; gap: 10px; align-items: center; padding: 12px 12px 8px; }
.feed-item-avatar { width: 32px; height: 32px; border-radius: 50%; overflow: hidden; flex-shrink: 0; border: 1px solid rgba(195,169,255,0.3); }
.feed-item-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }
.feed-item-avatar-placeholder { width: 32px; height: 32px; border-radius: 50%; background: rgba(93,59,140,0.3); border: 1px solid rgba(195,169,255,0.2); flex-shrink: 0; }
.feed-item-who { font-size: 12px; color: rgba(255,255,255,0.4); line-height: 1.4; }
.feed-item-seeker { color: #FFDFA3; font-weight: 500; cursor: pointer; }
.feed-item-seeker:hover { text-decoration: underline; }
.feed-item-action { color: rgba(255,255,255,0.3); }
.feed-item-reader { color: rgba(195,169,255,0.6); cursor: pointer; }
.feed-item-reader:hover { text-decoration: underline; color: #C3A9FF; }

/* Feed item reading card */
.feed-item-card { position: relative; }
.feed-item-img { width: 100%; height: 180px; object-fit: cover; display: block; }
.feed-item-title-overlay { position: absolute; bottom: 0; left: 0; right: 0; padding: 24px 12px 8px; background: linear-gradient(to top, rgba(10,0,21,0.9) 0%, rgba(10,0,21,0.4) 60%, transparent 100%); }
.feed-item-title { font-family: 'Cinzel', serif; font-size: 12px; font-weight: 500; color: #FFDFA3; margin: 0; line-height: 1.35; }

/* Feed item context + meta */
.feed-item-context { font-family: 'Cormorant Garamond', Georgia, serif; font-size: 13px; color: rgba(255,223,163,0.45); font-style: italic; line-height: 1.5; margin: 0; padding: 8px 12px 0; }
.feed-item-meta { display: flex; justify-content: space-between; padding: 6px 12px 10px; }
.feed-item-views { font-size: 10px; color: rgba(255,255,255,0.2); }
.feed-item-date { font-size: 10px; color: rgba(255,255,255,0.2); }

/* ─────────────────────────────────────────────────────────────
   The Lessons pane (the tab's scroll container; LessonsTab.vue inside)
   ───────────────────────────────────────────────────────────── */
/* height already reserves the 80px bottom nav — so only a small bottom pad here,
   else the content overflows by a nav's worth and shows a needless scrollbar. */
.lessons-pane { padding: 52px 0 20px; color: #E8D8FF; background: radial-gradient(120% 50% at 50% 0%, #1a0c33 0%, #0d0520 55%) #0d0520; overflow-y: auto; overflow-x: hidden; height: calc(100vh - 80px); box-sizing: border-box; }

/* A richer panel so the Featured Mystic hero stands out from the flat page.
   No fixed height — it stretches to the Top Mystics rail; the clamped teaser keeps
   content bounded so the two slides stay equal (the rail's height). */

/* Bound the teaser so a long one can't blow out the fixed height. */

/* Teaser + Read More stack; Read More opens the Mystic's welcome page. */

/* Featured-mystic showcase — the Mystic's portrait + name/meta, and their deck.
   Top-aligned so the cards sit centered against the portrait (not pulled down by
   the name/meta caption). */
/* Center the whole stack (name + showcase + teaser) so top/bottom margins are
   even — instead of the showcase eating the slack and shoving the teaser to the
   bottom edge. */

/* Slide-nav arrows flanking the hero at its vertical middle. */

/* Mystic name + meta, centered at the very top of the slide. */

/* space-evenly = equal gaps on the sides AND between, so the avatar + card set
   fill the width symmetrically (no wasted side margins). */

/* Reader/Mystic avatars are SQUARE (app convention). */

/* 3 sample majors as a small overlapping coverflow (same look as Just Arrived):
   the middle card spotlighted, the two sides smaller/faded and tucked behind. */

/* Center card matches the avatar height (200) so the set reads as the same size. */

/* Meet-the-Mystic CTA below the avatar. */

/* Visit Gallery — outline pill below the deck (same action as the cards). */

/* Bullets sit below the hero panel (outside the clickable surface). */

/* ── Just Arrived — the newest deck fanned out (tap a card to flip it face-up) ──
   The body flexes to fill the panel and centers the fan, but its content stays
   shorter than the Featured-Mystic slide so switching bullets never resizes the
   hero (the row already stretches to the Top Mystics rail). */
/* Just Arrived — chrome FLOATS over the card so the filmstrip fills the height. */

/* Deck card title stack — eyebrow ("Freshly Conjured Tarot Deck") above the deck
   name. Rendered IN-FLOW as a header band at the top of the card so it always
   sits ABOVE the filmstrip and can never overlap the spotlighted card. Generous
   side padding keeps the centered title clear of the floating top-corner chrome
   (by-line / View Full Gallery). */

/* Filmstrip fills the card (in-flow so it sets the height on mobile too; the
   chrome floats over it). */

/* Tiers — center fills most of the card height now that the chrome floats. */

/* Per-card CTA under the spotlighted card. */

/* Mystic: square avatar (Reader convention) with the name below it. */

@media (max-width: 560px) {

  /* On phones there's no room to float the corner chrome beside a centered
     title, so stack it all in-flow: by-line · title · gallery · filmstrip. */

}

/* The two daily community readings — Vibe + Most Pulled Card */
.vibe-card { display: flex; flex-direction: column; min-width: 0; }

.vibe-art { position: relative; height: 180px; background-size: cover; background-position: center; background-color: #0e0820; }
.vibe-art::after { content: ""; position: absolute; inset: 0; background: linear-gradient(to top, rgba(10,0,21,0.85), transparent 60%); }
.vibe-head { padding: 16px 18px 13px; display: flex; flex-direction: column; gap: 4px; align-items: center; text-align: center; }
.vibe-body { padding: 16px 18px 18px; display: flex; flex-direction: column; gap: 6px; }
.vibe-title { font-family: 'Cinzel', serif; font-size: 24px; color: #FFDFA3; line-height: 1.2; }
.vibe-sub { font-size: 11px; letter-spacing: 0.1em; text-transform: uppercase; color: rgba(195,169,255,0.6); }
.vibe-reading { font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic; font-size: 15px; color: rgba(232,216,255,0.8); line-height: 1.55; margin: 6px 0 0; }

.vibe-clickable:hover .vibe-readmore { text-decoration: underline; }
.vibe-clickable { cursor: pointer; transition: border-color 0.2s, background-color 0.2s, box-shadow 0.2s; }
.vibe-clickable:hover { border-color: rgba(255,223,163,0.45); background: rgba(255,255,255,0.06); box-shadow: 0 0 0 1px rgba(255,223,163,0.18) inset; }

/* Header clearance for the taller mobile tab bar. These live at the END of the
   stylesheet on purpose: `.discover-tabs-spacer` and `.lessons-pane` declare their
   base heights further down the file, so an earlier media query at the same
   specificity loses to them and silently does nothing. */
@media screen and (max-width: 560px) {
  /* The tab bar's height: its top clearance, a 44px tab, 8px. */
  .discover-tabs-spacer { height: calc(var(--tra-clear-top, 60px) + 52px); }
  .lessons-pane { padding-top: calc(var(--tra-clear-top, 60px) + 52px); }
  .community-wrap.cta-band .discover-tabs-spacer { height: 104px; }
  .community-wrap.cta-band .lessons-pane { padding-top: 104px; }
}
</style>
